import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FaChevronRight } from 'react-icons/fa'
import { useLocale } from '../../../../hooks/useLocale'
import { RatingQuestion as RatingQuestionType } from '../../../../types/end-journey'
import {
  leadershipValueScaleSelf,
  leadershipValueScaleSelfFujifilm,
} from '../../../../utils/ratings'
import { FUJIFILM_COMPANY_ID } from '../../../../app/constants'

interface RatingQuestionProps {
  userCompany: string
  name: string
  question: RatingQuestionType
  rules?: {
    required?: string | false
  }
}

export const RatingQuestion: React.FC<RatingQuestionProps> = ({
  userCompany,
  name,
  question,
}) => {
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false)
  const { t } = useLocale()

  const {
    register,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  // Set up a watcher to update the leadership value when the rating is selected
  useEffect(() => {
    // Watch for changes to the rating value
    const subscription = watch((value, { name: fieldName }) => {
      // Only process if the change is to our field
      if (fieldName === name && value[name]) {
        // Clear any validation errors when a value is selected
        clearErrors(name)

        // Set the leadership value field if applicable
        if (question.value?.value) {
          setValue(`${name}_leadershipValue`, question.value.value)
        }
      }
    })

    // Clean up the subscription when the component unmounts
    return () => subscription.unsubscribe()
  }, [name, question.value?.value, setValue, watch, clearErrors])

  const ratingScaleInstance =
    userCompany === FUJIFILM_COMPANY_ID
      ? leadershipValueScaleSelfFujifilm(t)
      : leadershipValueScaleSelf(t)

  // Watch the form field value
  const value = watch(name) as string | undefined
  // Get error from the form field
  const error = errors[name]?.message as string | undefined

  return (
    <div className="w-full max-w-2xl mx-auto px-6 pt-16 pb-2">
      <div className="space-y-6">
        {/* Title and Description */}
        <div className="space-y-2">
          <h2 className="text-gray-600">{question.title}</h2>
          <h3 className="text-2xl font-semibold text-gray-900">
            {question.value?.label}
            {/* {!question.optional && <span className="text-red-500 ml-1">*</span>} */}
          </h3>
          {question.value?.descriptionHtml && (
            <button
              type="button"
              onClick={() => setIsDescriptionModalOpen(true)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <span className="text-md">View description</span>
              <FaChevronRight className="w-3 h-3" />
            </button>
          )}

          {/* Description Modal */}
          {question.value?.descriptionHtml && (
            <Dialog
              open={isDescriptionModalOpen}
              onClose={() => setIsDescriptionModalOpen(false)}
              className="relative z-50"
            >
              <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
              />
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <DialogPanel
                    transition
                    className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                  >
                    <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                      <button
                        type="button"
                        onClick={() => setIsDescriptionModalOpen(false)}
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                      </button>
                    </div>

                    <DialogTitle
                      as="h2"
                      className="font-semibold leading-6 text-gray-900 text-lg"
                    >
                      {question.value?.label || 'Description'}
                    </DialogTitle>
                    <div className="w-full text-left text-gray-600 mt-8 space-y-1 px-4">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: question.value.descriptionHtml,
                        }}
                      />
                    </div>
                  </DialogPanel>
                </div>
              </div>
            </Dialog>
          )}
        </div>

        {/* Rating Options */}
        <div className="space-y-2">
          {ratingScaleInstance.map((option) => (
            <label key={option.level} className="block cursor-pointer">
              <input
                type="radio"
                className="sr-only"
                value={option.scale}
                {...register(name, {
                  required: 'Please select a rating to continue',
                })}
              />
              <div
                className={clsx(
                  'w-full p-4 text-center rounded-lg border transition-colors',
                  value === String(option.scale)
                    ? 'border-[#E85A2D] bg-[#FFF5F2] text-[#E85A2D]'
                    : 'border-gray-200 hover:bg-gray-50'
                )}
              >
                {option.level}
              </div>
            </label>
          ))}
          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        </div>

        {/* Optional Feedback */}

        <div className="space-y-2">
          <label className="block">
            <span className="text-gray-700 text-md">Why? (Optional)</span>
            <textarea
              {...register(`${name}_other`)}
              placeholder={
                question.otherTextPlaceholder ||
                'Share with us what influenced that score. Any specific helpful examples come to mind?'
              }
              rows={4}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#E85A2D] focus:border-[#E85A2D] resize-none"
            />
          </label>
        </div>

        {/* Error message moved up to be directly under the rating options */}
      </div>
    </div>
  )
}
